import { useState, useReducer, useCallback, useMemo } from 'react';

/**
 * Returns the unique key for a cart item. For variable products the
 * variation_id distinguishes different variations of the same parent;
 * for simple products the product id is used.
 */
function itemKey(product) {
  return product.variation_id ? product.variation_id : product.id;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const initialState = { items: [] };

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { product } = action.payload;
      const key = itemKey(product);
      const existing = state.items.find((i) => itemKey(i.product) === key);

      if (existing) {
        return {
          ...state,
          items: state.items.map((i) =>
            itemKey(i.product) === key
              ? { ...i, quantity: i.quantity + 1 }
              : i
          ),
        };
      }

      return {
        ...state,
        items: [
          ...state.items,
          {
            product,
            quantity: 1,
            discountType: '',
            discountValue: 0,
          },
        ],
      };
    }

    case 'REMOVE_ITEM': {
      const { key } = action.payload;
      return {
        ...state,
        items: state.items.filter((i) => itemKey(i.product) !== key),
      };
    }

    case 'UPDATE_QUANTITY': {
      const { key, quantity } = action.payload;
      const qty = Math.max(1, quantity);
      return {
        ...state,
        items: state.items.map((i) =>
          itemKey(i.product) === key ? { ...i, quantity: qty } : i
        ),
      };
    }

    case 'SET_LINE_DISCOUNT': {
      const { key, discountType, discountValue } = action.payload;
      return {
        ...state,
        items: state.items.map((i) =>
          itemKey(i.product) === key
            ? {
                ...i,
                discountType: discountType || '',
                discountValue: Math.max(0, Number(discountValue) || 0),
              }
            : i
        ),
      };
    }

    case 'CLEAR_CART':
      return { ...initialState };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCart() {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // -- Actions ---------------------------------------------------------------

  const addItem = useCallback((product) => {
    dispatch({ type: 'ADD_ITEM', payload: { product } });
  }, []);

  const removeItem = useCallback((product) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { key: itemKey(product) } });
  }, []);

  const updateQuantity = useCallback((product, quantity) => {
    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { key: itemKey(product), quantity },
    });
  }, []);

  const setLineDiscount = useCallback((product, discountType, discountValue) => {
    dispatch({
      type: 'SET_LINE_DISCOUNT',
      payload: { key: itemKey(product), discountType, discountValue },
    });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  // -- Computed values -------------------------------------------------------

  const { subtotal, discountTotal, taxTotal, total, itemCount } = useMemo(() => {
    const config = window.symplePOS || {};
    const taxRate = typeof config.taxRate === 'number' ? config.taxRate : 0;

    let sub = 0;
    let disc = 0;
    let count = 0;

    state.items.forEach((item) => {
      const price = parseFloat(item.product.price) || 0;
      const lineSubtotal = price * item.quantity;
      sub += lineSubtotal;
      count += item.quantity;

      if (item.discountType === 'percent' && item.discountValue > 0) {
        disc += lineSubtotal * (Math.min(item.discountValue, 100) / 100);
      } else if (item.discountType === 'fixed' && item.discountValue > 0) {
        disc += Math.min(item.discountValue * item.quantity, lineSubtotal);
      }
    });

    const taxable = Math.max(sub - disc, 0);
    const tax = taxRate > 0 ? taxable * (taxRate / 100) : 0;
    const grand = taxable + tax;

    return {
      subtotal: sub,
      discountTotal: disc,
      taxTotal: tax,
      total: grand,
      itemCount: count,
    };
  }, [state.items]);

  return {
    items: state.items,
    addItem,
    removeItem,
    updateQuantity,
    setLineDiscount,
    clearCart,
    subtotal,
    discountTotal,
    taxTotal,
    total,
    itemCount,
  };
}
